import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { TableCell, TableRow } from '@/components/ui/table';
import type {
  AggregatedPackage,
  AggregatedRepository,
  AggregatedUser,
  AgentCommandEvent,
  AgentCommandEventType,
} from '@kirin/api-client/contracts/agent-command-events';
import {
  FilterOperators,
  type FilterValue,
} from '@kirin/api-client/contracts/table';
import { EndpointSearchInput } from '@/components/shared/EndpointSearchInput';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  ENDPOINT_FILTER_FIELD,
  type EndpointDetails,
} from '@kirin/api-client/contracts/endpoints';
import { InventoryUserListDialog } from '@/pages/organization/components/InventoryUserListDialog';
import { EndpointsTable } from '@/components/Inventory/EndpointsTable';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { ClickableUserCount } from '@/components/shared/plugins/ClickableUserCount';
import { AgentBadge, CountButton } from './CellRenderers';
import { PACKAGE_VERSION_TOOLTIP } from './constants';
import {
  buildPackageGroupFilters,
  buildRepositoryGroupFilters,
  fetchGroupEvents,
  groupEventsQueryKey,
} from './api';
import { formatDateTime } from '@/lib/date-utils';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';

const DASH = '—';
const RECENT_COMMANDS_LIMIT = 10;
const OCCURRENCES_LIMIT = 100;

function text(value: string | null): string {
  return value && value.trim() ? value : DASH;
}

/**
 * The endpoint (machine) an occurrence ran on (KIR-3833 item 7): the resolved
 * registry hostname when known, else the raw machine id (font-mono, matching the
 * EndpointsTable "Endpoint ID" column), else a dash for unattributed events.
 */
function EndpointCell({
  machineId,
  endpointName,
}: {
  machineId: string | null;
  endpointName: string | null;
}): ReactElement {
  if (endpointName && endpointName.trim()) {
    return <span title={machineId ?? undefined}>{endpointName}</span>;
  }
  if (machineId) {
    return <span className="font-mono text-xs">{machineId}</span>;
  }
  return <span className="text-muted-foreground">{DASH}</span>;
}

function PackageVersionValue({
  version,
}: {
  version: string | null;
}): ReactElement {
  return (
    <span className="flex items-center gap-1.5">
      <span>{text(version)}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="How the version is determined"
            className="text-muted-foreground hover:text-foreground"
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {PACKAGE_VERSION_TOOLTIP}
        </TooltipContent>
      </Tooltip>
    </span>
  );
}

interface IdentityRow {
  label: string;
  value?: string | null;
  /**
   * A pre-rendered value (e.g. a canonical count link). When set it replaces the
   * plain text value — used for the Users / Occurrences / Endpoints drill-downs
   * so the detail dialog uses the same link components as the table cells
   * (KIR-3833: "follow the user link convention").
   */
  node?: ReactNode;
}

function Row({ label, value, node }: IdentityRow): ReactElement | null {
  if (!node && (!value || !value.trim())) return null;
  return (
    <div className="grid grid-cols-[120px_1fr] items-center gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      {node ? (
        <span className="justify-self-start">{node}</span>
      ) : (
        <span className="break-all">{value}</span>
      )}
    </div>
  );
}

/**
 * Distinct-users drill-down (KIR-3833). Reuses the project-wide
 * {@link InventoryUserListDialog} shell (same chrome as every inventory
 * "users with X" modal). The aggregated row already carries the user emails, so
 * there's no extra fetch — loading/error are always settled.
 */
function UsersDrillDownDialog({
  open,
  title,
  description,
  users,
  onClose,
}: {
  open: boolean;
  title: ReactNode;
  description: ReactNode;
  users: AggregatedUser[];
  onClose: () => void;
}): ReactElement {
  return (
    <InventoryUserListDialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      columns={[
        { label: 'User' },
        { label: 'Last Seen', className: 'w-[160px]' },
      ]}
      isLoading={false}
      error={null}
      isEmpty={users.length === 0}
      emptyMessage="No users recorded."
    >
      {users.map((user, i) => (
        <TableRow key={user.email ?? `no-email-${i}`}>
          <TableCell className="text-sm">
            {user.email ?? (
              <span className="text-muted-foreground">No email on file</span>
            )}
          </TableCell>
          <TableCell className="text-sm">
            <RelativeTimeWithTooltip date={user.lastSeen} />
          </TableCell>
        </TableRow>
      ))}
    </InventoryUserListDialog>
  );
}

/**
 * Distinct-endpoints drill-down (KIR-3833). Renders the project-wide
 * {@link EndpointsTable} (same table as the Inventory → Users and Alerts
 * endpoint dialogs), fed the endpoint details already on the aggregated row —
 * no fetch.
 */
function EndpointsDrillDownDialog({
  open,
  title,
  endpoints,
  onClose,
}: {
  open: boolean;
  title: ReactNode;
  endpoints: EndpointDetails[];
  onClose: () => void;
}): ReactElement {
  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Distinct endpoints (machines) this ran on.
          </DialogDescription>
        </DialogHeader>
        <EndpointsTable endpoints={endpoints} />
      </DialogContent>
    </Dialog>
  );
}

/** Shared "raw commands (N most recent)" block for a group. */
function RawCommandsList({
  events,
  isLoading,
}: {
  events: AgentCommandEvent[];
  isLoading: boolean;
}): ReactElement {
  if (isLoading) {
    return <p className="mt-1 text-xs text-muted-foreground">Loading…</p>;
  }
  if (events.length === 0) {
    return (
      <p className="mt-1 text-xs text-muted-foreground">
        No commands recorded.
      </p>
    );
  }
  return (
    <div className="mt-1 flex flex-col gap-2">
      {events.map(event => (
        <pre
          key={event.id}
          className="max-w-full whitespace-pre-wrap break-all rounded-md bg-muted p-3 text-xs"
        >
          {event.rawCommand}
        </pre>
      ))}
    </div>
  );
}

/** Shared unaggregated-occurrences table (agent / user / command / timestamp). */
function OccurrencesTable({
  events,
  isLoading,
}: {
  events: AgentCommandEvent[];
  isLoading: boolean;
}): ReactElement {
  const timeZone = useDisplayTimeZone();
  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Loading…</p>;
  }
  return (
    <table className="w-full text-left text-sm">
      <thead className="text-muted-foreground">
        <tr>
          <th className="whitespace-nowrap py-2 pr-4 font-medium">Agent</th>
          <th className="whitespace-nowrap py-2 pr-4 font-medium">User</th>
          <th className="whitespace-nowrap py-2 pr-4 font-medium">Endpoint</th>
          <th className="w-full py-2 pr-4 font-medium">Command</th>
          <th className="whitespace-nowrap py-2 font-medium">Timestamp</th>
        </tr>
      </thead>
      <tbody>
        {events.map(event => (
          <tr key={event.id} className="border-t align-top">
            <td className="whitespace-nowrap py-2 pr-4">
              <AgentBadge agentName={event.agentName} />
            </td>
            <td className="whitespace-nowrap py-2 pr-4">
              {text(event.userEmail)}
            </td>
            <td className="whitespace-nowrap py-2 pr-4">
              <EndpointCell
                machineId={event.machineId}
                endpointName={event.endpointName}
              />
            </td>
            <td className="py-2 pr-4">
              <code className="whitespace-pre-wrap break-words text-xs">
                {event.rawCommand}
              </code>
            </td>
            <td className="whitespace-nowrap py-2 pr-2 text-xs tabular-nums">
              {formatDateTime(event.timestamp, timeZone, event.timestamp)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Fetch a group's unaggregated events; disabled until `open`. */
function useGroupEvents(
  open: boolean,
  organizationId: string | undefined,
  eventType: AgentCommandEventType,
  filters: FilterValue[],
  limit: number
): { events: AgentCommandEvent[]; isLoading: boolean; isError: boolean } {
  const { data, isLoading, isError } = useQuery({
    queryKey: open
      ? groupEventsQueryKey(organizationId, eventType, filters, { limit })
      : ['group-events', 'idle'],
    queryFn: () =>
      fetchGroupEvents(organizationId, eventType, filters, { limit }),
    enabled: open,
  });
  return {
    events: data?.events ?? [],
    isLoading: open && isLoading,
    isError: open && isError,
  };
}

/**
 * Generic row-click detail modal for an aggregated group (KIR-3807): identity
 * rows + the 10 most recent raw commands (lazy-fetched on open). Shared by the
 * package and repository views.
 */
function GroupDetailDialog({
  open,
  title,
  subtitle,
  knownBad,
  identityRows,
  organizationId,
  eventType,
  filters,
  onClose,
}: {
  open: boolean;
  title: string;
  subtitle: string;
  knownBad: boolean;
  identityRows: IdentityRow[];
  organizationId: string | undefined;
  eventType: AgentCommandEventType;
  filters: FilterValue[];
  onClose: () => void;
}): ReactElement {
  const { events, isLoading } = useGroupEvents(
    open,
    organizationId,
    eventType,
    filters,
    RECENT_COMMANDS_LIMIT
  );
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent
        className="max-w-2xl"
        // Default autofocus lands on the first tooltip trigger (Version info /
        // Users count) and opens its tooltip; focus the panel instead so the
        // tooltip shows on hover only.
        onOpenAutoFocus={e => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {title}
            {knownBad && (
              <Badge variant="destructive" className="uppercase">
                Known bad
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>
        {open && (
          <div className="flex min-w-0 flex-col gap-2">
            {identityRows.map(r => (
              <Row
                key={r.label}
                label={r.label}
                value={r.value}
                node={r.node}
              />
            ))}
            <div className="mt-2">
              <span className="text-sm text-muted-foreground">
                Raw commands (10 most recent)
              </span>
              <RawCommandsList events={events} isLoading={isLoading} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Generic occurrences drill-down (unaggregated events for one group). */
function GroupOccurrencesDialog({
  open,
  subtitle,
  organizationId,
  eventType,
  filters,
  onClose,
}: {
  open: boolean;
  subtitle: ReactNode;
  organizationId: string | undefined;
  eventType: AgentCommandEventType;
  filters: FilterValue[];
  onClose: () => void;
}): ReactElement {
  // KIR-5098: endpoint name / ID search, server-side so it reaches past the
  // first OCCURRENCES_LIMIT rows.
  const [endpointQuery, setEndpointQuery] = useState('');
  const debouncedTyped = useDebouncedValue(endpointQuery.trim());
  const debouncedQuery = endpointQuery.trim() ? debouncedTyped : '';
  const searchFilters = useMemo(
    (): FilterValue[] =>
      debouncedQuery
        ? [
            ...filters,
            {
              field: ENDPOINT_FILTER_FIELD,
              operator: FilterOperators.CONTAINS,
              value: [debouncedQuery],
            },
          ]
        : filters,
    [filters, debouncedQuery]
  );
  const { events, isLoading, isError } = useGroupEvents(
    open,
    organizationId,
    eventType,
    searchFilters,
    OCCURRENCES_LIMIT
  );
  const close = (): void => {
    setEndpointQuery('');
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && close()}>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Occurrences</DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>
        <EndpointSearchInput
          value={endpointQuery}
          onChange={setEndpointQuery}
        />
        <div className="max-h-[60vh] overflow-auto">
          {isError ? (
            <p className="text-xs text-muted-foreground">
              Failed to load occurrences
            </p>
          ) : !isLoading && debouncedQuery && events.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No occurrences on a matching endpoint
            </p>
          ) : (
            <OccurrencesTable events={events} isLoading={isLoading} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Package wrappers ────────────────────────────────────────────────────────

export function AggregatedPackageDetailDialog({
  organizationId,
  pkg,
  onClose,
  onShowUsers,
  onShowOccurrences,
  onShowEndpoints,
}: {
  organizationId: string | undefined;
  pkg: AggregatedPackage | null;
  onClose: () => void;
  /** Drill-down openers so aggregate values in the dialog are clickable (KIR-3833). */
  onShowUsers?: (pkg: AggregatedPackage) => void;
  onShowOccurrences?: (pkg: AggregatedPackage) => void;
  onShowEndpoints?: (pkg: AggregatedPackage) => void;
}): ReactElement {
  const timeZone = useDisplayTimeZone();
  return (
    <GroupDetailDialog
      open={!!pkg}
      title="Package install"
      subtitle={`Aggregated across ${pkg?.occurrenceCount ?? 0} occurrences.`}
      knownBad={pkg?.knownBad ?? false}
      identityRows={[
        { label: 'Package', value: pkg?.packageName ?? null },
        {
          label: 'Version',
          node: pkg && <PackageVersionValue version={pkg.packageVersion} />,
        },
        { label: 'Manager', value: pkg?.packageManager ?? null },
        { label: 'Source', value: pkg?.sourceUrl ?? null },
        { label: 'Project', value: pkg?.projectPath ?? null },
        {
          label: 'Users',
          node: pkg && (
            <ClickableUserCount
              count={pkg.userCount}
              tooltipScope="with this package"
              onClick={() => onShowUsers?.(pkg)}
            />
          ),
        },
        {
          label: 'Occurrences',
          node: pkg && (
            <CountButton
              count={pkg.occurrenceCount}
              label={`Show ${pkg.occurrenceCount} occurrences`}
              onClick={() => onShowOccurrences?.(pkg)}
            />
          ),
        },
        {
          label: 'Endpoints',
          node: pkg && (
            <CountButton
              count={pkg.endpointCount}
              label={`Show ${pkg.endpointCount} endpoints`}
              onClick={() => onShowEndpoints?.(pkg)}
            />
          ),
        },
        {
          label: 'Last seen',
          value: pkg ? formatDateTime(pkg.lastSeen, timeZone) : null,
        },
      ]}
      organizationId={organizationId}
      eventType="package"
      filters={pkg ? buildPackageGroupFilters(pkg) : []}
      onClose={onClose}
    />
  );
}

AggregatedPackageDetailDialog.displayName = 'AggregatedPackageDetailDialog';

export function PackageOccurrencesDialog({
  organizationId,
  pkg,
  onClose,
}: {
  organizationId: string | undefined;
  pkg: AggregatedPackage | null;
  onClose: () => void;
}): ReactElement {
  return (
    <GroupOccurrencesDialog
      open={!!pkg}
      subtitle={
        <>
          {pkg?.packageName}
          {pkg?.packageVersion ? `@${pkg.packageVersion}` : ''} — most recent{' '}
          {OCCURRENCES_LIMIT} installs.
        </>
      }
      organizationId={organizationId}
      eventType="package"
      filters={pkg ? buildPackageGroupFilters(pkg) : []}
      onClose={onClose}
    />
  );
}

PackageOccurrencesDialog.displayName = 'PackageOccurrencesDialog';

export function PackageUsersDialog({
  pkg,
  onClose,
}: {
  pkg: AggregatedPackage | null;
  onClose: () => void;
}): ReactElement {
  return (
    <UsersDrillDownDialog
      open={!!pkg}
      title="Users"
      description={`Users who installed ${pkg?.packageName ?? 'this package'}.`}
      users={pkg?.users ?? []}
      onClose={onClose}
    />
  );
}

PackageUsersDialog.displayName = 'PackageUsersDialog';

export function PackageEndpointsDialog({
  pkg,
  onClose,
}: {
  pkg: AggregatedPackage | null;
  onClose: () => void;
}): ReactElement {
  return (
    <EndpointsDrillDownDialog
      open={!!pkg}
      title={`Endpoints where ${pkg?.packageName ?? 'this package'} was installed`}
      endpoints={pkg?.endpoints ?? []}
      onClose={onClose}
    />
  );
}

PackageEndpointsDialog.displayName = 'PackageEndpointsDialog';

// ── Repository wrappers ─────────────────────────────────────────────────────

export function AggregatedRepositoryDetailDialog({
  organizationId,
  repo,
  onClose,
  onShowUsers,
  onShowOccurrences,
  onShowEndpoints,
}: {
  organizationId: string | undefined;
  repo: AggregatedRepository | null;
  onClose: () => void;
  /** Drill-down openers so aggregate values in the dialog are clickable (KIR-3833). */
  onShowUsers?: (repo: AggregatedRepository) => void;
  onShowOccurrences?: (repo: AggregatedRepository) => void;
  onShowEndpoints?: (repo: AggregatedRepository) => void;
}): ReactElement {
  const timeZone = useDisplayTimeZone();
  return (
    <GroupDetailDialog
      open={!!repo}
      title="Repository access"
      subtitle={`Aggregated across ${repo?.occurrenceCount ?? 0} occurrences.`}
      knownBad={repo?.knownBad ?? false}
      identityRows={[
        { label: 'Repository', value: repo?.repoUrl ?? null },
        { label: 'Action', value: repo?.action ?? null },
        { label: 'Branch', value: repo?.branch ?? null },
        { label: 'Project', value: repo?.projectPath ?? null },
        {
          label: 'Users',
          node: repo && (
            <ClickableUserCount
              count={repo.userCount}
              tooltipScope="with this repository"
              onClick={() => onShowUsers?.(repo)}
            />
          ),
        },
        {
          label: 'Occurrences',
          node: repo && (
            <CountButton
              count={repo.occurrenceCount}
              label={`Show ${repo.occurrenceCount} occurrences`}
              onClick={() => onShowOccurrences?.(repo)}
            />
          ),
        },
        {
          label: 'Endpoints',
          node: repo && (
            <CountButton
              count={repo.endpointCount}
              label={`Show ${repo.endpointCount} endpoints`}
              onClick={() => onShowEndpoints?.(repo)}
            />
          ),
        },
        {
          label: 'Last seen',
          value: repo ? formatDateTime(repo.lastSeen, timeZone) : null,
        },
      ]}
      organizationId={organizationId}
      eventType="git"
      filters={repo ? buildRepositoryGroupFilters(repo) : []}
      onClose={onClose}
    />
  );
}

AggregatedRepositoryDetailDialog.displayName =
  'AggregatedRepositoryDetailDialog';

export function RepositoryOccurrencesDialog({
  organizationId,
  repo,
  onClose,
}: {
  organizationId: string | undefined;
  repo: AggregatedRepository | null;
  onClose: () => void;
}): ReactElement {
  return (
    <GroupOccurrencesDialog
      open={!!repo}
      subtitle={
        <>
          {repo?.repoUrl}
          {repo?.branch ? ` (${repo.branch})` : ''} — most recent{' '}
          {OCCURRENCES_LIMIT} accesses.
        </>
      }
      organizationId={organizationId}
      eventType="git"
      filters={repo ? buildRepositoryGroupFilters(repo) : []}
      onClose={onClose}
    />
  );
}

RepositoryOccurrencesDialog.displayName = 'RepositoryOccurrencesDialog';

export function RepositoryUsersDialog({
  repo,
  onClose,
}: {
  repo: AggregatedRepository | null;
  onClose: () => void;
}): ReactElement {
  return (
    <UsersDrillDownDialog
      open={!!repo}
      title="Users"
      description={`Users who accessed ${repo?.repoUrl ?? 'this repository'}.`}
      users={repo?.users ?? []}
      onClose={onClose}
    />
  );
}

RepositoryUsersDialog.displayName = 'RepositoryUsersDialog';

export function RepositoryEndpointsDialog({
  repo,
  onClose,
}: {
  repo: AggregatedRepository | null;
  onClose: () => void;
}): ReactElement {
  return (
    <EndpointsDrillDownDialog
      open={!!repo}
      title={`Endpoints that accessed ${repo?.repoUrl ?? 'this repository'}`}
      endpoints={repo?.endpoints ?? []}
      onClose={onClose}
    />
  );
}

RepositoryEndpointsDialog.displayName = 'RepositoryEndpointsDialog';
